import { apiEndpoint } from "../utils/env";

async function fetchWithAuth(url, token, options = {}) {
  const headers = options.headers || {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  headers["Content-Type"] = headers["Content-Type"] || "application/json";
  const res = await fetch(url, { ...options, headers });
  const txt = await res.text();
  let json = null;
  try {
    json = txt ? JSON.parse(txt) : null;
  } catch {
    json = txt;
  }
  if (!res.ok) {
    const errMsg =
      json && json.message
        ? json.message
        : typeof json === "string"
        ? json
        : res.statusText;
    const error = new Error(errMsg || `Request failed: ${res.status}`);
    // Attach structured fields if provided by the API
    error.code = json && json.code ? json.code : null;
    error.status = res.status;
    error.details = json;
    throw error;
  }
  return json;
}

export async function getDatabases(token) {
  const url = apiEndpoint("/api/Admin/GetDatabases");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function getMe(token) {
  const url = apiEndpoint("/api/Me");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function getDatabaseIndexes(token, databaseId) {
  const url = apiEndpoint(
    `/api/Admin/GetDatabaseIndexes?databaseId=${encodeURIComponent(databaseId)}`
  );
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function addDatabaseIndexes(token, databaseId, indexes) {
  const url = apiEndpoint("/api/Admin/AddDatabaseIndexes");
  const body = { databaseId, indexes };
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function deleteDatabaseIndex(token, databaseId, fieldName) {
  const url = apiEndpoint("/api/Admin/DeleteDatabaseIndex");
  const body = { databaseId, fieldName };
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function updateDatabaseIndex(token, databaseId, fieldName, dataType, inputFormat, length) {
  const url = apiEndpoint("/api/Admin/UpdateDatabaseIndex");
  return fetchWithAuth(url, token, {
    method: "POST",
    // `length` (optional) widens the column to that size; omitted → metadata-only edit.
    body: JSON.stringify({ databaseId, fieldName, dataType, inputFormat, length }),
  });
}

// Queue an asynchronous build of a real (physical) database index on a column.
export async function createColumnIndex(token, databaseId, fieldName) {
  const url = apiEndpoint("/api/Admin/CreateColumnIndex");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ databaseId, fieldName }),
  });
}

// Drop the real (physical) database index on a column.
export async function dropColumnIndex(token, databaseId, fieldName) {
  const url = apiEndpoint("/api/Admin/DropColumnIndex");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ databaseId, fieldName }),
  });
}

// Fill/normalize the per-document FileExtension column for a document base.
// Dry-run by default: returns { needyCount, updated, examined, truncated, sample } without writing.
export async function backfillFileExtensions(token, databaseId, { dryRun = true, batchSize = 1000, maxRows = 0 } = {}) {
  const url = apiEndpoint("/api/Admin/BackfillFileExtensions");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ databaseId, dryRun, batchSize, maxRows }),
  });
}

export async function setDatabaseClosed(token, databaseId, isClosed) {
  const url = apiEndpoint("/api/Admin/SetDatabaseClosed");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ databaseId, isClosed }),
  });
}

// ── Storage locations (per-database storage backends) ──────────────────────────
export async function getStorageLocations(token) {
  const url = apiEndpoint("/api/Admin/StorageLocations");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function createStorageLocation(token, location) {
  const url = apiEndpoint("/api/Admin/StorageLocation");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify(location),
  });
}

export async function updateStorageLocation(token, location) {
  const url = apiEndpoint("/api/Admin/UpdateStorageLocation");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify(location),
  });
}

export async function deleteStorageLocation(token, storageLocationId) {
  const url = apiEndpoint("/api/Admin/DeleteStorageLocation");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ storageLocationId }),
  });
}

// Bind a database to a storage location, or pass null to unbind (use the global default).
export async function setDatabaseStorageLocation(token, databaseId, storageLocationId) {
  const url = apiEndpoint("/api/Admin/SetDatabaseStorageLocation");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ databaseId, storageLocationId }),
  });
}

export async function deleteDatabase(token, databaseId) {
  const url = apiEndpoint("/api/Admin/DeleteDatabase");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ databaseId }),
  });
}

export async function purgeDatabase(token, databaseId) {
  const url = apiEndpoint("/api/Admin/PurgeDatabase");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ databaseId }),
  });
}

export async function createDatabase(token, databaseName, partitionByMonth) {
  const url = apiEndpoint("/api/Admin/CreateDatabase");
  const body = { databaseName };
  if (partitionByMonth != null) body.partitionByMonth = partitionByMonth;
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// Locked-down (DML-only) Oracle: activate a Pending document base once its
// physical table has been provisioned by a DBA.
export async function activateDatabase(token, databaseId) {
  const url = apiEndpoint("/api/Admin/ActivateDatabase");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ databaseId }),
  });
}

// Locked-down (DML-only) Oracle: re-fetch the owner-run provisioning script for a
// Pending document base (the create dialog shows it only once, then it's gone).
export async function getProvisioningScript(token, databaseId) {
  const url = apiEndpoint("/api/Admin/GetProvisioningScript");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ databaseId }),
  });
}

export async function updateDatabase(token, request) {
  const url = apiEndpoint("/api/Admin/UpdateDatabase");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function updateDatabaseProperties(token, databaseId, properties) {
  const url = apiEndpoint("/api/Admin/UpdateDatabaseProperties");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ databaseId, properties }),
  });
}

// mode: "soft" (default — hide documents, keep files) or "hard" (also delete archive + index files).
// confirmation: required for "hard" mode, must be the literal string "DELETE" (typed by the user
// in the confirmation modal). Server rejects hard requests without it.
export async function deleteBatchProcess(token, id, mode = "soft", confirmation) {
  const url = apiEndpoint("/api/Admin/DeleteBatchProcess");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ id, mode, confirmation }),
  });
}

// Server-side paginated + filtered + sorted. Returns { total, counts, items }.
//   skip/take   — page window (take capped at 200 server-side)
//   status      — one of Pending|InProgress|Completed|Failed (active-view narrowing)
//   search      — matches database, user, file type, path, or exact id
//   from/to     — CreatedDate range (ISO strings)
//   showDeleted — true = trash view (soft-deleted, files not yet cleaned)
//   sort/dir    — sort column (created|id|database|status) + direction (asc|desc)
export async function getBatchProcesses(token, {
  skip = 0, take = 100, status, search, from, to, showDeleted, sort, dir,
} = {}) {
  const params = new URLSearchParams();
  params.append("skip", String(skip));
  params.append("take", String(take));
  if (status) params.append("status", status);
  if (search) params.append("search", search);
  if (from) params.append("from", from);
  if (to) params.append("to", to);
  if (showDeleted) params.append("showDeleted", "true");
  if (sort) params.append("sort", sort);
  if (dir) params.append("dir", dir);
  const url = apiEndpoint(`/api/Admin/GetBatchProcesses?${params.toString()}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

// Lightweight single-batch live-progress poll (indexed PK read). Returns just the
// worker-written fields the detail panel needs while a batch is InProgress.
export async function getBatchProcessLive(token, id) {
  const url = apiEndpoint(`/api/Admin/GetBatchProcessLive/${id}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

// Database Provider Configuration APIs
export async function getDatabaseConfiguration(token) {
  const url = apiEndpoint("/api/Admin/GetDatabaseConfiguration");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function updateDatabaseConfiguration(token, config) {
  const url = apiEndpoint("/api/Admin/UpdateDatabaseConfiguration");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify(config),
  });
}

export async function testDatabaseConnection(token, config) {
  const url = apiEndpoint("/api/Admin/TestDatabaseConnection");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify(config),
  });
}

export async function migrateDataToOracle(token) {
  const url = apiEndpoint("/api/Admin/MigrateDataToOracle");
  return fetchWithAuth(url, token, { method: "POST" });
}

export async function logLogin(token) {
  const url = apiEndpoint("/api/Me/LogLogin");
  return fetchWithAuth(url, token, { method: "POST" });
}

export async function restoreBatchProcess(token, id) {
  const url = apiEndpoint("/api/Admin/RestoreBatchProcess");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ id }),
  });
}

// Empty trash queues hard delete for every soft-deleted batch. Requires the same
// "DELETE" confirmation token as a single hard delete — server rejects without it.
export async function emptyTrash(token, confirmation) {
  const url = apiEndpoint("/api/Admin/EmptyTrash");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ confirmation }),
  });
}

export async function getAuditLogUsers(token) {
  const url = apiEndpoint("/api/Admin/GetAuditLogUsers");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function getBatchProcessLogs(token, batchId) {
  const url = apiEndpoint(`/api/Admin/GetBatchProcessLogs/${batchId}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function getStorageInfoForBatch(token, batchId) {
  const url = apiEndpoint(`/api/Admin/GetStorageInfoForBatch/${batchId}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function getAuditLogs(token, { skip = 0, take = 50, userName, action, search, from, to } = {}) {
  const params = new URLSearchParams();
  params.append("skip", String(skip));
  params.append("take", String(take));
  if (userName) params.append("userName", userName);
  if (action) params.append("action", action);
  if (search) params.append("search", search);
  if (from) params.append("from", from);
  if (to) params.append("to", to);
  const url = apiEndpoint(`/api/Admin/GetAuditLogs?${params.toString()}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function getContentAccessLogs(token, { skip = 0, take = 50, userName, action, kind, dbName, search, from, to } = {}) {
  const params = new URLSearchParams();
  params.append("skip", String(skip));
  params.append("take", String(take));
  if (userName) params.append("userName", userName);
  if (action) params.append("action", action);
  if (kind) params.append("kind", kind);
  if (dbName) params.append("dbName", dbName);
  if (search) params.append("search", search);
  if (from) params.append("from", from);
  if (to) params.append("to", to);
  const url = apiEndpoint(`/api/Admin/GetContentAccessLogs?${params.toString()}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function getContentAccessLogUsers(token) {
  const url = apiEndpoint("/api/Admin/GetContentAccessLogUsers");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function verifyContentAccessIntegrity(token) {
  const url = apiEndpoint("/api/Admin/VerifyContentAccessIntegrity");
  return fetchWithAuth(url, token, { method: "GET" });
}

// Server-side paginated + filtered. Returns { total, activeTotal, items }.
export async function getContentHolds(token, {
  databaseId, activeOnly, search, skip = 0, take = 100,
} = {}) {
  const params = new URLSearchParams();
  params.append("skip", String(skip));
  params.append("take", String(take));
  if (databaseId) params.append("databaseId", String(databaseId));
  if (activeOnly) params.append("activeOnly", "true");
  if (search) params.append("search", search);
  const url = apiEndpoint(`/api/Admin/ContentHolds?${params.toString()}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function placeContentHold(token, body) {
  const url = apiEndpoint("/api/Admin/ContentHold");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(body) });
}

export async function liftContentHold(token, id, reason) {
  const url = apiEndpoint(`/api/Admin/ContentHold/${encodeURIComponent(id)}/Lift`);
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify({ reason }) });
}

// ── Defensible disposition (governed certified deletion) ─────────────────────

export async function previewDisposition(token, body) {
  const url = apiEndpoint("/api/Admin/Disposition/Preview");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(body) });
}

export async function getDispositions(token, { databaseId, status, openOnly, skip = 0, take = 50 } = {}) {
  const params = new URLSearchParams();
  if (databaseId) params.append("databaseId", String(databaseId));
  if (status) params.append("status", status);
  if (openOnly) params.append("openOnly", "true");
  params.append("skip", String(skip));
  params.append("take", String(take));
  const url = apiEndpoint(`/api/Admin/Dispositions?${params.toString()}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function proposeDisposition(token, body) {
  const url = apiEndpoint("/api/Admin/Disposition");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(body) });
}

export async function approveDisposition(token, id, note) {
  const url = apiEndpoint(`/api/Admin/Disposition/${encodeURIComponent(id)}/Approve`);
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify({ note }) });
}

export async function rejectDisposition(token, id, reason) {
  const url = apiEndpoint(`/api/Admin/Disposition/${encodeURIComponent(id)}/Reject`);
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify({ reason }) });
}

export async function executeDisposition(token, id) {
  const url = apiEndpoint(`/api/Admin/Disposition/${encodeURIComponent(id)}/Execute`);
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify({}) });
}

export async function getDispositionCertificate(token, id) {
  const url = apiEndpoint(`/api/Admin/Disposition/${encodeURIComponent(id)}/Certificate`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function getDispositionPublicKey(token) {
  const url = apiEndpoint("/api/Admin/Disposition/PublicKey");
  return fetchWithAuth(url, token, { method: "GET" });
}

// ── Access governance (opt-in grant enforcement) ─────────────────────────────

export async function getAccessPolicy(token, organization) {
  const qs = organization ? `?organization=${encodeURIComponent(organization)}` : "";
  const url = apiEndpoint(`/api/Admin/AccessPolicy${qs}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function setAccessPolicy(token, { organization, enforceGrants }) {
  const url = apiEndpoint("/api/Admin/AccessPolicy");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify({ organization, enforceGrants }) });
}

export async function getUserRoleAssignments(token, { roleId, userName } = {}) {
  const params = new URLSearchParams();
  if (roleId) params.append("roleId", String(roleId));
  if (userName) params.append("userName", userName);
  const qs = params.toString();
  const url = apiEndpoint(`/api/Admin/UserRoleAssignments${qs ? "?" + qs : ""}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function createUserRoleAssignment(token, { userName, roleId }) {
  const url = apiEndpoint("/api/Admin/UserRoleAssignment");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify({ userName, roleId }) });
}

export async function setUserRoleAssignmentActive(token, id, isActive) {
  const url = apiEndpoint(`/api/Admin/UserRoleAssignment/${encodeURIComponent(id)}/SetActive`);
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify({ isActive }) });
}

export async function getMyAccess(token) {
  const url = apiEndpoint("/api/Admin/AccessControl/MyAccess");
  return fetchWithAuth(url, token, { method: "GET" });
}

// ── Access reviews / recertification ─────────────────────────────────────────

export async function createAccessReview(token, body) {
  const url = apiEndpoint("/api/Admin/AccessReviewCampaign");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(body) });
}

export async function getAccessReviewCampaigns(token, { status, skip = 0, take = 100 } = {}) {
  const params = new URLSearchParams();
  if (status) params.append("status", status);
  params.append("skip", String(skip));
  params.append("take", String(take));
  const url = apiEndpoint(`/api/Admin/AccessReviewCampaigns?${params.toString()}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function getAccessReviewCampaign(token, id) {
  const url = apiEndpoint(`/api/Admin/AccessReviewCampaign/${encodeURIComponent(id)}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function decideAccessReviewItem(token, itemId, decision, note) {
  const url = apiEndpoint(`/api/Admin/AccessReviewItem/${encodeURIComponent(itemId)}/Decide`);
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify({ decision, note }) });
}

export async function completeAccessReview(token, id) {
  const url = apiEndpoint(`/api/Admin/AccessReviewCampaign/${encodeURIComponent(id)}/Complete`);
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify({}) });
}

export async function cancelAccessReview(token, id) {
  const url = apiEndpoint(`/api/Admin/AccessReviewCampaign/${encodeURIComponent(id)}/Cancel`);
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify({}) });
}

export async function getAttestationCertificate(token, id) {
  const url = apiEndpoint(`/api/Admin/AccessReviewCampaign/${encodeURIComponent(id)}/Certificate`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function getDatabaseStats(token, databaseId) {
  const url = apiEndpoint(`/api/Admin/GetDatabaseStats?databaseId=${encodeURIComponent(databaseId)}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

// Bulk per-database document counts for the dashboard DOCUMENTS widget.
// Returns { totalDocuments, databases: [{ databaseId, name, displayName, documentCount }] }.
export async function getDatabaseDocumentCounts(token) {
  const url = apiEndpoint("/api/Admin/GetDatabaseDocumentCounts");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function browseRecords(token, { databaseId, offset = 0, pageSize = 50, sortOrder = "desc" }) {
  const url = apiEndpoint("/api/Admin/BrowseRecords");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ databaseId, offset, pageSize, sortOrder }),
  });
}

// ── Ingest File Type Definitions ─────────────────────────────────────────────

export async function getIngestFileTypes(token) {
  const url = apiEndpoint("/api/Admin/GetIngestFileTypes");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function createIngestFileType(token, { typeId, label, mode, sortOrder }) {
  const url = apiEndpoint("/api/Admin/CreateIngestFileType");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ typeId, label, mode, sortOrder }),
  });
}

export async function updateIngestFileType(token, { id, typeId, label, mode, sortOrder, isActive }) {
  const url = apiEndpoint("/api/Admin/UpdateIngestFileType");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ id, typeId, label, mode, sortOrder, isActive }),
  });
}

export async function deleteIngestFileType(token, id) {
  const url = apiEndpoint("/api/Admin/DeleteIngestFileType");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ id }),
  });
}

// ── Output File Type Definitions ──────────────────────────────────────────────

export async function getOutputFileTypes(token) {
  const url = apiEndpoint("/api/Admin/GetOutputFileTypes");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function createOutputFileType(token, { typeId, label, sortOrder }) {
  const url = apiEndpoint("/api/Admin/CreateOutputFileType");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ typeId, label, sortOrder }),
  });
}

export async function updateOutputFileType(token, { id, typeId, label, sortOrder, isActive }) {
  const url = apiEndpoint("/api/Admin/UpdateOutputFileType");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ id, typeId, label, sortOrder, isActive }),
  });
}

export async function deleteOutputFileType(token, id) {
  const url = apiEndpoint("/api/Admin/DeleteOutputFileType");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ id }),
  });
}

// ── Conversion API Definitions ────────────────────────────────────────────────

export async function getConversionApis(token) {
  const url = apiEndpoint("/api/Admin/GetConversionApis");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function createConversionApi(token, data) {
  const url = apiEndpoint("/api/Admin/CreateConversionApi");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(data) });
}

export async function updateConversionApi(token, data) {
  const url = apiEndpoint("/api/Admin/UpdateConversionApi");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(data) });
}

export async function deleteConversionApi(token, id) {
  const url = apiEndpoint("/api/Admin/DeleteConversionApi");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify({ id }) });
}

export async function getSystemLogs(token, { levels, source, nodeId, instanceId, from, to, q, cursor, pageSize = 100 } = {}) {
  const params = new URLSearchParams();
  params.append("pageSize", String(pageSize));
  if (levels && levels.length > 0) levels.forEach((l) => params.append("level", l));
  if (source) params.append("source", source);
  if (nodeId) params.append("nodeId", nodeId);
  if (instanceId) params.append("instanceId", instanceId);
  if (from)   params.append("from", from);
  if (to)     params.append("to", to);
  if (q)      params.append("q", q);
  if (cursor && cursor.ts && cursor.id != null) {
    params.append("cursorTs", cursor.ts);
    params.append("cursorId", String(cursor.id));
  }
  const url = apiEndpoint(`/api/Admin/GetSystemLogs?${params.toString()}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function purgeSystemLogs(token, olderThanDays = 30) {
  const url = apiEndpoint(`/api/Admin/PurgeSystemLogs?olderThanDays=${olderThanDays}`);
  return fetchWithAuth(url, token, { method: "DELETE" });
}

export async function exportSystemConfig(token) {
  const url = apiEndpoint("/api/Admin/ExportSystemConfig");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function importSystemConfig(token, config) {
  const url = apiEndpoint("/api/Admin/ImportSystemConfig");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ config }),
  });
}

export async function exportDefinitionsConfig(token) {
  const url = apiEndpoint("/api/Admin/ExportDefinitionsConfig");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function importDefinitionsConfig(token, config) {
  const url = apiEndpoint("/api/Admin/ImportDefinitionsConfig");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ config }),
  });
}

export async function exportDatabaseConfig(token, databaseId) {
  const url = apiEndpoint(`/api/Admin/ExportDatabaseConfig?databaseId=${encodeURIComponent(databaseId)}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function importDatabaseConfig(token, databaseId, config, rebuildFastSearchIndexes = false) {
  const url = apiEndpoint("/api/Admin/ImportDatabaseConfig");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ databaseId, config, rebuildFastSearchIndexes }),
  });
}

export async function exportDatabasesBundle(token, databaseIds) {
  const params = Array.isArray(databaseIds) && databaseIds.length > 0
    ? `?ids=${encodeURIComponent(databaseIds.join(","))}`
    : "";
  const url = apiEndpoint(`/api/Admin/ExportDatabasesBundle${params}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function importDatabasesBundle(token, bundle, createMissing = true, rebuildFastSearchIndexes = false) {
  const url = apiEndpoint("/api/Admin/ImportDatabasesBundle");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify({ bundle, createMissing, rebuildFastSearchIndexes }),
  });
}


// ── Monitoring / Metrics ──────────────────────────────────────────────────────

export async function getMetricsSummary(token, hours = 1) {
  const url = apiEndpoint(`/api/metrics/summary?hours=${hours}`);
  return fetchWithAuth(url, token);
}

export async function getMetricsTimeline(token, hours = 6, bucketMinutes = 10) {
  const url = apiEndpoint(`/api/metrics/timeline?hours=${hours}&bucketMinutes=${bucketMinutes}`);
  return fetchWithAuth(url, token);
}

export async function getMetricsEndpoints(token, hours = 24) {
  const url = apiEndpoint(`/api/metrics/endpoints?hours=${hours}`);
  return fetchWithAuth(url, token);
}

export async function getMetricsErrors(token, take = 20) {
  const url = apiEndpoint(`/api/metrics/errors?take=${take}`);
  return fetchWithAuth(url, token);
}

export async function getMetricsDistribution(token, hours = 24) {
  const url = apiEndpoint(`/api/metrics/distribution?hours=${hours}`);
  return fetchWithAuth(url, token);
}

// Database bootstrap status — anonymous so it's reachable even when the auth
// schema is unavailable during an Oracle/SQL Server outage.
export async function getDbStatus() {
  const url = apiEndpoint("/api/admin/db-status");
  return fetchWithAuth(url, null);
}

// ── Lifecycle Rules (retention/deletion) ──────────────────────────────
// Endpoints on AdminController (api/Admin). Rules are organization-scoped server-side.

export async function getLifecycleRules(token, databaseId) {
  const url = apiEndpoint(`/api/Admin/LifecycleRules?databaseId=${encodeURIComponent(databaseId)}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function createLifecycleRule(token, rule) {
  const url = apiEndpoint("/api/Admin/LifecycleRule");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(rule) });
}

export async function updateLifecycleRule(token, rule) {
  const url = apiEndpoint("/api/Admin/LifecycleRule");
  return fetchWithAuth(url, token, { method: "PUT", body: JSON.stringify(rule) });
}

export async function deleteLifecycleRule(token, ruleId) {
  const url = apiEndpoint(`/api/Admin/LifecycleRule/${encodeURIComponent(ruleId)}`);
  return fetchWithAuth(url, token, { method: "DELETE" });
}

export async function previewLifecycleRule(token, ruleId) {
  const url = apiEndpoint(`/api/Admin/LifecycleRule/${encodeURIComponent(ruleId)}/Preview`);
  return fetchWithAuth(url, token, { method: "POST" });
}

export async function applyLifecycleRule(token, ruleId) {
  const url = apiEndpoint(`/api/Admin/LifecycleRule/${encodeURIComponent(ruleId)}/Apply`);
  return fetchWithAuth(url, token, { method: "POST" });
}

export async function getLifecycleRuns(token, databaseId, ruleId) {
  const q = ruleId
    ? `?databaseId=${encodeURIComponent(databaseId)}&ruleId=${encodeURIComponent(ruleId)}`
    : `?databaseId=${encodeURIComponent(databaseId)}`;
  const url = apiEndpoint(`/api/Admin/LifecycleRuns${q}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function setRetentionGuard(token, payload) {
  const url = apiEndpoint("/api/Admin/SetRetentionGuard");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(payload) });
}

// Revert a successful tier Move run (relocate its chunks back to the source backend).
export async function revertLifecycleRun(token, runId) {
  const url = apiEndpoint(`/api/Admin/LifecycleRun/${encodeURIComponent(runId)}/Revert`);
  return fetchWithAuth(url, token, { method: "POST" });
}

// ── Partition conversion (existing-table monthly partitioning) ─────────────
// Metadata-only assessment: is this database's table eligible to convert to a monthly
// DocumentDate-partitioned layout? Persists + returns the assessment row.
export async function runPartitionAssessment(token, databaseId) {
  const url = apiEndpoint("/api/Admin/PartitionAssessment");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify({ databaseId }) });
}

// Enqueue an offline conversion (Status="Pending") if eligible; otherwise returns the
// Ineligible/Blocked assessment. Dedups an already Pending/Running conversion.
export async function enqueuePartitionConvert(token, databaseId) {
  const url = apiEndpoint("/api/Admin/PartitionConvert");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify({ databaseId }) });
}

// Prior assessments/conversions for a database (most recent first).
export async function getPartitionConversions(token, databaseId) {
  const url = apiEndpoint(`/api/Admin/PartitionConversions?databaseId=${encodeURIComponent(databaseId)}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

// Cross-database lifecycle events (track & search). `filters` keys: dbId, strategy, status,
// trigger, search, fromUtc, toUtc, page, pageSize — all optional.
export async function getLifecycleEvents(token, filters = {}) {
  const qs = Object.entries(filters)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&");
  const url = apiEndpoint(`/api/Admin/LifecycleEvents${qs ? "?" + qs : ""}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

// ── Document enrichment (AI Document Request pillar) ─────────────────────────
// Which document bases have their documents read and understood. Off for every
// base until somebody turns it on, one at a time - there is deliberately no
// "enable everywhere" call here, because a sidecar row per document is
// affordable for a DMS-shaped base and ruinous for a print archive.

export async function getEnrichmentBases(token) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Enrichment/Bases"), token, { method: "GET" });
}

export async function setEnrichmentBase(token, databaseId, enabled) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Enrichment/Base"), token, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ databaseId, enabled }),
  });
}

// ── Content classification (v1 rules engine) ──────────────────────────────────
// Deterministic, org-scoped rules that label documents at ingest; labels drive
// retention/disposition via a lifecycle rule's ConditionJson.

export async function getClassificationRules(token, { databaseId } = {}) {
  const qs = databaseId ? `?databaseId=${encodeURIComponent(databaseId)}` : "";
  const url = apiEndpoint(`/api/Admin/ClassificationRules${qs}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function createClassificationRule(token, body) {
  const url = apiEndpoint("/api/Admin/ClassificationRule");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(body) });
}

export async function updateClassificationRule(token, id, body) {
  const url = apiEndpoint(`/api/Admin/ClassificationRule/${encodeURIComponent(id)}`);
  return fetchWithAuth(url, token, { method: "PUT", body: JSON.stringify(body) });
}

export async function deleteClassificationRule(token, id) {
  const url = apiEndpoint(`/api/Admin/ClassificationRule/${encodeURIComponent(id)}`);
  return fetchWithAuth(url, token, { method: "DELETE" });
}

export async function testClassificationRule(token, body) {
  const url = apiEndpoint("/api/Admin/ClassificationRule/Test");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(body) });
}

export async function getClassificationStarters(token) {
  const url = apiEndpoint("/api/Admin/Classification/Starters");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function cloneClassificationStarter(token, id, overrides = {}) {
  const url = apiEndpoint(`/api/Admin/Classification/Starters/${encodeURIComponent(id)}/Clone`);
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(overrides) });
}

export async function triggerClassificationBackfill(token, body) {
  const url = apiEndpoint("/api/Admin/Classification/Backfill");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(body) });
}

export async function getClassificationBackfillStatus(token, databaseId) {
  const url = apiEndpoint(`/api/Admin/Classification/Backfill/${encodeURIComponent(databaseId)}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function cancelClassificationBackfill(token, databaseId) {
  const url = apiEndpoint(`/api/Admin/Classification/Backfill/${encodeURIComponent(databaseId)}/Cancel`);
  return fetchWithAuth(url, token, { method: "POST" });
}

// Per-document classification override (auditable label correction).
export async function getDocumentClassification(token, databaseId, archiveNo) {
  const url = apiEndpoint(`/api/Admin/Classification/Document?databaseId=${encodeURIComponent(databaseId)}&archiveNo=${encodeURIComponent(archiveNo)}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function overrideDocumentClassification(token, body) {
  const url = apiEndpoint("/api/Admin/Classification/Document/Override");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(body) });
}

export async function getDocumentClassificationHistory(token, databaseId, archiveNo) {
  const url = apiEndpoint(`/api/Admin/Classification/Document/History?databaseId=${encodeURIComponent(databaseId)}&archiveNo=${encodeURIComponent(archiveNo)}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

// ─────────────────────────────────────────────────────────────────────────────
// Content organization — folders, taxonomy terms, content types (pillar: DMS)
// All gated content-governance, organization-scoped on the server.
// ─────────────────────────────────────────────────────────────────────────────

// Folders
export async function getFolders(token, { includeInactive = false, organization } = {}) {
  const p = new URLSearchParams();
  if (includeInactive) p.set("includeInactive", "true");
  if (organization) p.set("organization", organization);
  const qs = p.toString() ? `?${p.toString()}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/Folders${qs}`), token, { method: "GET" });
}
export async function createFolder(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Folder"), token, { method: "POST", body: JSON.stringify(body) });
}
export async function updateFolder(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Folder/${encodeURIComponent(id)}`), token, { method: "PUT", body: JSON.stringify(body) });
}
export async function moveFolder(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Folder/${encodeURIComponent(id)}/Move`), token, { method: "POST", body: JSON.stringify(body) });
}
export async function archiveFolder(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Folder/${encodeURIComponent(id)}`), token, { method: "DELETE" });
}
export async function restoreFolder(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Folder/${encodeURIComponent(id)}/Restore`), token, { method: "POST" });
}
export async function getDocumentFolder(token, databaseId, archiveNo) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Document/Folder?databaseId=${encodeURIComponent(databaseId)}&archiveNo=${encodeURIComponent(archiveNo)}`), token, { method: "GET" });
}
export async function setDocumentFolder(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Document/Folder"), token, { method: "POST", body: JSON.stringify(body) });
}
export async function triggerFolderBackfill(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Folder/Backfill"), token, { method: "POST", body: JSON.stringify(body) });
}
export async function getFolderBackfillStatus(token, databaseId) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Folder/Backfill/${encodeURIComponent(databaseId)}`), token, { method: "GET" });
}
export async function cancelFolderBackfill(token, databaseId) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Folder/Backfill/${encodeURIComponent(databaseId)}/Cancel`), token, { method: "POST" });
}
export async function getFolderDocuments(token, folderId, { organization, includeSubtree = true, max = 200 } = {}) {
  const p = new URLSearchParams();
  if (organization) p.set("organization", organization);
  if (!includeSubtree) p.set("includeSubtree", "false");
  if (max) p.set("max", String(max));
  const qs = p.toString() ? `?${p.toString()}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/Folder/${encodeURIComponent(folderId)}/Documents${qs}`), token, { method: "GET" });
}

// Workspace starter template — the folder tree every new user is given on first sign-in.
// An organization with no rows here falls back to the built-in default set, so this is optional
// configuration rather than something that must be filled in before workspaces work.
export async function getWorkspaceTemplate(token, { includeInactive = false, organization, locale } = {}) {
  const p = new URLSearchParams();
  if (includeInactive) p.set("includeInactive", "true");
  if (organization) p.set("organization", organization);
  // Names the built-in preview in the admin's own language — the same language a user in it is provisioned in.
  if (locale) p.set("locale", locale);
  const qs = p.toString() ? `?${p.toString()}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/WorkspaceTemplate${qs}`), token, { method: "GET" });
}
export async function createWorkspaceTemplateFolder(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/WorkspaceTemplate"), token, { method: "POST", body: JSON.stringify(body) });
}
export async function updateWorkspaceTemplateFolder(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/WorkspaceTemplate/${encodeURIComponent(id)}`), token, { method: "PUT", body: JSON.stringify(body) });
}
export async function archiveWorkspaceTemplateFolder(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/WorkspaceTemplate/${encodeURIComponent(id)}`), token, { method: "DELETE" });
}
export async function seedWorkspaceTemplate(token, locale) {
  const qs = locale ? `?locale=${encodeURIComponent(locale)}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/WorkspaceTemplate/SeedFromDefault${qs}`), token, { method: "POST" });
}

// Taxonomy terms
export async function getTaxonomyNodes(token, { includeInactive = false, organization } = {}) {
  const p = new URLSearchParams();
  if (includeInactive) p.set("includeInactive", "true");
  if (organization) p.set("organization", organization);
  const qs = p.toString() ? `?${p.toString()}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/TaxonomyNodes${qs}`), token, { method: "GET" });
}
export async function createTaxonomyNode(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/TaxonomyNode"), token, { method: "POST", body: JSON.stringify(body) });
}
export async function updateTaxonomyNode(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/TaxonomyNode/${encodeURIComponent(id)}`), token, { method: "PUT", body: JSON.stringify(body) });
}
export async function moveTaxonomyNode(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/TaxonomyNode/${encodeURIComponent(id)}/Move`), token, { method: "POST", body: JSON.stringify(body) });
}
export async function archiveTaxonomyNode(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/TaxonomyNode/${encodeURIComponent(id)}`), token, { method: "DELETE" });
}
export async function restoreTaxonomyNode(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/TaxonomyNode/${encodeURIComponent(id)}/Restore`), token, { method: "POST" });
}
export async function getDocumentTerms(token, databaseId, archiveNo) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Document/Taxonomy?databaseId=${encodeURIComponent(databaseId)}&archiveNo=${encodeURIComponent(archiveNo)}`), token, { method: "GET" });
}
export async function addDocumentTerm(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Document/Taxonomy"), token, { method: "POST", body: JSON.stringify(body) });
}
export async function removeDocumentTerm(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Document/Taxonomy/Remove"), token, { method: "POST", body: JSON.stringify(body) });
}

// Content types
export async function getContentTypes(token, { includeInactive = false, organization } = {}) {
  const p = new URLSearchParams();
  if (includeInactive) p.set("includeInactive", "true");
  if (organization) p.set("organization", organization);
  const qs = p.toString() ? `?${p.toString()}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/ContentTypes${qs}`), token, { method: "GET" });
}
export async function createContentType(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/ContentType"), token, { method: "POST", body: JSON.stringify(body) });
}
export async function updateContentType(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/ContentType/${encodeURIComponent(id)}`), token, { method: "PUT", body: JSON.stringify(body) });
}
export async function archiveContentType(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/ContentType/${encodeURIComponent(id)}`), token, { method: "DELETE" });
}
export async function getBaseContentType(token, databaseId) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/DatabaseBase/${encodeURIComponent(databaseId)}/ContentType`), token, { method: "GET" });
}
export async function setBaseContentType(token, databaseId, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/DatabaseBase/${encodeURIComponent(databaseId)}/ContentType`), token, { method: "POST", body: JSON.stringify(body) });
}

// Starter template packs (folder/taxonomy/content-type bundles by vertical × region)
export async function getTemplates(token, { vertical, region, locale } = {}) {
  const p = new URLSearchParams();
  if (vertical) p.set("vertical", vertical);
  if (region) p.set("region", region);
  if (locale) p.set("locale", locale);
  const qs = p.toString() ? `?${p.toString()}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/Templates${qs}`), token, { method: "GET" });
}
export async function getTemplate(token, id, locale) {
  const qs = locale ? `?locale=${encodeURIComponent(locale)}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/Templates/${encodeURIComponent(id)}${qs}`), token, { method: "GET" });
}
export async function applyTemplate(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Templates/${encodeURIComponent(id)}/Apply`), token, { method: "POST", body: JSON.stringify(body || {}) });
}

// Clear / reset content organization for an org (safe undo of a template apply)
export async function getContentOrgSummary(token, organization) {
  const qs = organization ? `?organization=${encodeURIComponent(organization)}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/ContentOrganization/Summary${qs}`), token, { method: "GET" });
}
export async function clearContentOrg(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/ContentOrganization/Clear"), token, { method: "POST", body: JSON.stringify(body) });
}

// ── Document versioning (gated content-governance) ────────────────────────────────────
// A version chain = doc rows sharing a VersionGroupId; the newest is the current version.
export async function getDocumentVersions(token, databaseName, documentId) {
  const url = apiEndpoint(
    `/api/Document/Versions?databaseName=${encodeURIComponent(databaseName)}&documentId=${encodeURIComponent(documentId)}`
  );
  return fetchWithAuth(url, token, { method: "GET" });
}
export async function checkOutVersion(token, databaseName, documentId) {
  return fetchWithAuth(apiEndpoint("/api/Document/Version/CheckOut"), token, {
    method: "POST",
    body: JSON.stringify({ databaseName, documentId }),
  });
}
export async function cancelCheckOutVersion(token, databaseName, documentId) {
  return fetchWithAuth(apiEndpoint("/api/Document/Version/CheckIn"), token, {
    method: "POST",
    body: JSON.stringify({ databaseName, documentId }),
  });
}
export async function restoreDocumentVersion(token, databaseName, documentId, comment) {
  return fetchWithAuth(apiEndpoint("/api/Document/Version/Restore"), token, {
    method: "POST",
    body: JSON.stringify({ databaseName, documentId, comment }),
  });
}
export async function uploadDocumentVersion(token, { databaseName, documentId, fileName, fileBase64, comment }) {
  return fetchWithAuth(apiEndpoint("/api/Document/Version/Upload"), token, {
    method: "POST",
    body: JSON.stringify({ databaseName, documentId, fileName, fileBase64, comment }),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Records management — the FILE PLAN (record-series tree), each series' retention
// schedule, declaring documents into a series, and the cutoff event that starts a
// retention clock. All gated content-governance and organization-scoped on the
// server. Retention itself is executed by the lifecycle engine under the existing
// maker-checker disposition workflow — this surface only configures and declares.

export async function getRecordCategories(token, { includeInactive = false, organization } = {}) {
  const params = new URLSearchParams();
  if (includeInactive) params.set("includeInactive", "true");
  if (organization) params.set("organization", organization);
  const qs = params.toString();
  const url = apiEndpoint(`/api/Admin/RecordCategories${qs ? `?${qs}` : ""}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function createRecordCategory(token, body) {
  const url = apiEndpoint("/api/Admin/RecordCategory");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(body) });
}

export async function updateRecordCategory(token, id, body) {
  const url = apiEndpoint(`/api/Admin/RecordCategory/${encodeURIComponent(id)}`);
  return fetchWithAuth(url, token, { method: "PUT", body: JSON.stringify(body) });
}

export async function moveRecordCategory(token, id, body) {
  const url = apiEndpoint(`/api/Admin/RecordCategory/${encodeURIComponent(id)}/Move`);
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(body) });
}

export async function archiveRecordCategory(token, id, organization) {
  const qs = organization ? `?organization=${encodeURIComponent(organization)}` : "";
  const url = apiEndpoint(`/api/Admin/RecordCategory/${encodeURIComponent(id)}${qs}`);
  return fetchWithAuth(url, token, { method: "DELETE" });
}

export async function restoreRecordCategory(token, id, organization) {
  const qs = organization ? `?organization=${encodeURIComponent(organization)}` : "";
  const url = apiEndpoint(`/api/Admin/RecordCategory/${encodeURIComponent(id)}/Restore${qs}`);
  return fetchWithAuth(url, token, { method: "POST" });
}

/// Sets a series' retention schedule. The response reports whether the schedule is
/// actually ENFORCED (a lifecycle rule was generated) and, when it is not, why.
export async function setRecordSchedule(token, id, body) {
  const url = apiEndpoint(`/api/Admin/RecordCategory/${encodeURIComponent(id)}/Schedule`);
  return fetchWithAuth(url, token, { method: "PUT", body: JSON.stringify(body) });
}

// ── Auto-declare rules (the optional zero-touch filing path) ──────────────────

export async function getRecordSeriesRules(token, { databaseId, organization } = {}) {
  const params = new URLSearchParams();
  if (databaseId) params.set("databaseId", databaseId);
  if (organization) params.set("organization", organization);
  const qs = params.toString();
  const url = apiEndpoint(`/api/Admin/RecordSeriesRules${qs ? `?${qs}` : ""}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function createRecordSeriesRule(token, body) {
  const url = apiEndpoint("/api/Admin/RecordSeriesRule");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(body) });
}

export async function updateRecordSeriesRule(token, id, body) {
  const url = apiEndpoint(`/api/Admin/RecordSeriesRule/${encodeURIComponent(id)}`);
  return fetchWithAuth(url, token, { method: "PUT", body: JSON.stringify(body) });
}

export async function deleteRecordSeriesRule(token, id) {
  const url = apiEndpoint(`/api/Admin/RecordSeriesRule/${encodeURIComponent(id)}`);
  return fetchWithAuth(url, token, { method: "DELETE" });
}

export async function testRecordSeriesRule(token, body) {
  const url = apiEndpoint("/api/Admin/RecordSeriesRule/Test");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(body) });
}

// ── Per-document declaration ──────────────────────────────────────────────────

export async function getDocumentRecord(token, databaseId, archiveNo) {
  const url = apiEndpoint(
    `/api/Admin/Document/Record?databaseId=${encodeURIComponent(databaseId)}&archiveNo=${encodeURIComponent(archiveNo)}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

/// Declares a document into a series, or UNDECLARES it when recordCategoryId is null.
export async function declareDocument(token, body) {
  const url = apiEndpoint("/api/Admin/Document/Record");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(body) });
}

export async function setDocumentCutoff(token, body) {
  const url = apiEndpoint("/api/Admin/Document/Record/Cutoff");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(body) });
}

// ── Bulk declare / cutoff jobs ────────────────────────────────────────────────

export async function triggerRecordsBackfill(token, body) {
  const url = apiEndpoint("/api/Admin/Records/Backfill");
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(body) });
}

export async function getRecordsBackfillStatus(token, databaseId) {
  const url = apiEndpoint(`/api/Admin/Records/Backfill/${encodeURIComponent(databaseId)}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function cancelRecordsBackfill(token, databaseId) {
  const url = apiEndpoint(`/api/Admin/Records/Backfill/${encodeURIComponent(databaseId)}/Cancel`);
  return fetchWithAuth(url, token, { method: "POST" });
}

// ── eDiscovery: matters, collection, review coding, redaction, production ─────

export async function getLegalCases(token, status) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/LegalCases${qs}`), token, { method: "GET" });
}

export async function getLegalCase(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}`), token, { method: "GET" });
}

export async function createLegalCase(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/LegalCase"), token, { method: "POST", body: JSON.stringify(body) });
}

export async function closeLegalCase(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/Close`), token,
    { method: "POST", body: JSON.stringify(body || {}) });
}

// ── Custodian scoping — matter custodians, and the per-database column that carries custodian identity ──

export async function getLegalCustodians(token, id, includeInactive = false) {
  const qs = includeInactive ? "?includeInactive=true" : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/Custodians${qs}`), token,
    { method: "GET" });
}

export async function createLegalCustodian(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/Custodians`), token,
    { method: "POST", body: JSON.stringify(body) });
}

export async function updateLegalCustodian(token, id, custodianId, body) {
  return fetchWithAuth(
    apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/Custodian/${encodeURIComponent(custodianId)}`), token,
    { method: "PUT", body: JSON.stringify(body) });
}

export async function deactivateLegalCustodian(token, id, custodianId) {
  return fetchWithAuth(
    apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/Custodian/${encodeURIComponent(custodianId)}`), token,
    { method: "DELETE" });
}

export async function getCustodianFieldMaps(token, { databaseId, includeInactive } = {}) {
  const p = new URLSearchParams();
  if (databaseId) p.set("databaseId", databaseId);
  if (includeInactive) p.set("includeInactive", "true");
  const qs = p.toString();
  return fetchWithAuth(apiEndpoint(`/api/Admin/CustodianFieldMaps${qs ? `?${qs}` : ""}`), token, { method: "GET" });
}

export async function createCustodianFieldMap(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/CustodianFieldMaps"), token,
    { method: "POST", body: JSON.stringify(body) });
}

export async function updateCustodianFieldMap(token, mapId, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/CustodianFieldMap/${encodeURIComponent(mapId)}`), token,
    { method: "PUT", body: JSON.stringify(body) });
}

export async function deactivateCustodianFieldMap(token, mapId) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/CustodianFieldMap/${encodeURIComponent(mapId)}`), token,
    { method: "DELETE" });
}

export async function previewLegalCollect(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/Collect/Preview`), token,
    { method: "POST", body: JSON.stringify(body) });
}

export async function runLegalCollect(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/Collect`), token,
    { method: "POST", body: JSON.stringify(body) });
}

export async function getLegalCaseItems(token, id, { coding, skip = 0, take = 100 } = {}) {
  const p = new URLSearchParams();
  if (coding) p.set("coding", coding);
  p.set("skip", String(skip));
  p.set("take", String(take));
  return fetchWithAuth(apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/Items?${p}`), token, { method: "GET" });
}

export async function codeLegalItems(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/Code`), token,
    { method: "POST", body: JSON.stringify(body) });
}

export async function getPrivilegeLog(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/PrivilegeLog`), token, { method: "GET" });
}

export async function addRedactionMark(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/Redaction`), token,
    { method: "POST", body: JSON.stringify(body) });
}

export async function deleteRedactionMark(token, id, markId) {
  return fetchWithAuth(
    apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/Redaction/${encodeURIComponent(markId)}`),
    token, { method: "DELETE" });
}

export async function getProductions(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/Productions`), token, { method: "GET" });
}

export async function proposeProduction(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/Production/Propose`), token,
    { method: "POST", body: JSON.stringify(body) });
}

export async function previewProduction(token, id, setId) {
  return fetchWithAuth(
    apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/Production/${encodeURIComponent(setId)}/Preview`),
    token, { method: "GET" });
}

export async function approveProduction(token, id, setId, body) {
  return fetchWithAuth(
    apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/Production/${encodeURIComponent(setId)}/Approve`),
    token, { method: "POST", body: JSON.stringify(body || {}) });
}

export async function rejectProduction(token, id, setId, body) {
  return fetchWithAuth(
    apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/Production/${encodeURIComponent(setId)}/Reject`),
    token, { method: "POST", body: JSON.stringify(body || {}) });
}

export async function produceProduction(token, id, setId) {
  return fetchWithAuth(
    apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/Production/${encodeURIComponent(setId)}/Produce`),
    token, { method: "POST" });
}

export async function getProductionCertificate(token, id, setId) {
  return fetchWithAuth(
    apiEndpoint(`/api/Admin/LegalCase/${encodeURIComponent(id)}/Production/${encodeURIComponent(setId)}/Certificate`),
    token, { method: "GET" });
}

// ── Workflow / BPM ──────────────────────────────────────────────────────────────────────────
// Admin surface (definitions, graphs, auto-start rules, instances). Acting on a task lives on the
// non-admin /api/Workflow surface — see workflowApi.js — because approvers are ordinary users.

export async function getWorkflowDefinitions(token, status) {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/Workflow/Definitions${q}`), token, { method: "GET" });
}

export async function getWorkflowDefinition(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Workflow/Definition/${encodeURIComponent(id)}`), token, { method: "GET" });
}

export async function createWorkflowDefinition(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Workflow/Definition"), token, {
    method: "POST", body: JSON.stringify(body),
  });
}

export async function saveWorkflowGraph(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Workflow/Definition/${encodeURIComponent(id)}/Graph`), token, {
    method: "PUT", body: JSON.stringify(body),
  });
}

export async function validateWorkflowDefinition(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Workflow/Definition/${encodeURIComponent(id)}/Validate`), token, { method: "POST" });
}

export async function activateWorkflowDefinition(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Workflow/Definition/${encodeURIComponent(id)}/Activate`), token, { method: "POST" });
}

export async function archiveWorkflowDefinition(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Workflow/Definition/${encodeURIComponent(id)}/Archive`), token, { method: "POST" });
}

/**
 * Who this organization already routes work to — recents and frequents for the designer's people picker.
 *
 * Distinct from the identity directory (`searchDirectory` in directoryApi.js): this is drawn from workflow
 * history rather than the account store, so it opens the picker on a short, already-relevant list instead of
 * the whole staff directory. Names come back unresolved; the caller pairs them with directory entries to show
 * a full name, and shows the bare user name when there is no match (a deleted account, or an external one).
 */
export async function getWorkflowAssigneeSuggestions(token, take = 8) {
  return fetchWithAuth(
    apiEndpoint(`/api/Admin/Workflow/AssigneeSuggestions?take=${encodeURIComponent(take)}`),
    token,
    { method: "GET" }
  );
}

export async function getWorkflowStartRules(token) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Workflow/StartRules"), token, { method: "GET" });
}

export async function createWorkflowStartRule(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Workflow/StartRule"), token, {
    method: "POST", body: JSON.stringify(body),
  });
}

export async function updateWorkflowStartRule(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Workflow/StartRule/${encodeURIComponent(id)}`), token, {
    method: "PUT", body: JSON.stringify(body),
  });
}

export async function deleteWorkflowStartRule(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Workflow/StartRule/${encodeURIComponent(id)}`), token, { method: "DELETE" });
}

export async function testWorkflowStartRules(token, fields, opts = {}) {
  const params = new URLSearchParams();
  if (opts.dbInfoId) params.set("dbInfoId", opts.dbInfoId);
  if (opts.fileName) params.set("fileName", opts.fileName);
  const q = params.toString() ? `?${params}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/Workflow/StartRules/Test${q}`), token, {
    method: "POST", body: JSON.stringify(fields || {}),
  });
}

export async function getWorkflowInstances(token, opts = {}) {
  const params = new URLSearchParams();
  if (opts.status) params.set("status", opts.status);
  if (opts.definitionId) params.set("definitionId", opts.definitionId);
  if (opts.skip != null) params.set("skip", opts.skip);
  if (opts.take != null) params.set("take", opts.take);
  const q = params.toString() ? `?${params}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/Workflow/Instances${q}`), token, { method: "GET" });
}

export async function getWorkflowInstance(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Workflow/Instance/${encodeURIComponent(id)}`), token, { method: "GET" });
}

export async function startWorkflowInstance(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Workflow/Start"), token, {
    method: "POST", body: JSON.stringify(body),
  });
}

export async function bulkStartWorkflow(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Workflow/BulkStart"), token, {
    method: "POST", body: JSON.stringify(body),
  });
}

export async function cancelWorkflowInstance(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Workflow/Instance/${encodeURIComponent(id)}/Cancel`), token, {
    method: "POST", body: JSON.stringify(body || {}),
  });
}

// ── Delivery / self-service portal (gated delivery-portal) ────────────────────────────
// Two responses here carry a secret that exists exactly once: issueDeliveryAccessCode returns
// the access code, and createDeliveryGrant returns the link token. Neither can be read back —
// the server stored only a hash — so callers must surface them to the operator immediately.

export async function getDeliveryPolicy(token, organization) {
  const q = organization ? `?organization=${encodeURIComponent(organization)}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/Policy${q}`), token, { method: "GET" });
}

export async function saveDeliveryPolicy(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Delivery/Policy"), token, {
    method: "PUT", body: JSON.stringify(body || {}),
  });
}

/* ── Portal branding ─────────────────────────────────────────────────────────────────
   What a corporation puts on the portal its own customers land on. The palette in the
   response is resolved server-side — the same derivation the portal itself paints with —
   so the preview on the admin screen cannot drift from what a customer will see. */

export async function getDeliveryBranding(token, organization) {
  const q = organization ? `?organization=${encodeURIComponent(organization)}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/Branding${q}`), token, { method: "GET" });
}

export async function saveDeliveryBranding(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Delivery/Branding"), token, {
    method: "PUT", body: JSON.stringify(body || {}),
  });
}

/** Resolves a draft into the palette it would produce. Writes nothing. */
export async function previewDeliveryBranding(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Delivery/Branding/Preview"), token, {
    method: "POST", body: JSON.stringify(body || {}),
  });
}

export async function getDeliveryFieldMaps(token, opts = {}) {
  const params = new URLSearchParams();
  if (opts.dbInfoId) params.set("dbInfoId", opts.dbInfoId);
  if (opts.includeInactive) params.set("includeInactive", "true");
  const q = params.toString() ? `?${params}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/FieldMaps${q}`), token, { method: "GET" });
}

export async function createDeliveryFieldMap(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Delivery/FieldMaps"), token, {
    method: "POST", body: JSON.stringify(body || {}),
  });
}

export async function updateDeliveryFieldMap(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/FieldMap/${encodeURIComponent(id)}`), token, {
    method: "PUT", body: JSON.stringify(body || {}),
  });
}

export async function deleteDeliveryFieldMap(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/FieldMap/${encodeURIComponent(id)}`), token, { method: "DELETE" });
}

export async function getDeliveryRecipients(token, opts = {}) {
  const params = new URLSearchParams();
  if (opts.organization) params.set("organization", opts.organization);
  if (opts.q) params.set("q", opts.q);
  if (opts.includeDisabled) params.set("includeDisabled", "true");
  if (opts.take != null) params.set("take", opts.take);
  const q = params.toString() ? `?${params}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/Recipients${q}`), token, { method: "GET" });
}

export async function createDeliveryRecipient(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Delivery/Recipients"), token, {
    method: "POST", body: JSON.stringify(body || {}),
  });
}

export async function updateDeliveryRecipient(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/Recipient/${encodeURIComponent(id)}`), token, {
    method: "PUT", body: JSON.stringify(body || {}),
  });
}

/** Returns { accessCode } — shown once, never retrievable again. */
export async function issueDeliveryAccessCode(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/Recipient/${encodeURIComponent(id)}/AccessCode`), token, { method: "POST" });
}

export async function clearDeliveryAccessCode(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/Recipient/${encodeURIComponent(id)}/AccessCode`), token, { method: "DELETE" });
}

export async function unlockDeliveryRecipient(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/Recipient/${encodeURIComponent(id)}/Unlock`), token, { method: "POST" });
}

export async function getDeliveryRecipientKeys(token, id, includeInactive) {
  const q = includeInactive ? "?includeInactive=true" : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/Recipient/${encodeURIComponent(id)}/Keys${q}`), token, { method: "GET" });
}

export async function addDeliveryRecipientKey(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/Recipient/${encodeURIComponent(id)}/Keys`), token, {
    method: "POST", body: JSON.stringify(body || {}),
  });
}

export async function deleteDeliveryRecipientKey(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/RecipientKey/${encodeURIComponent(id)}`), token, { method: "DELETE" });
}

export async function getDeliveryIssuers(token, organization) {
  const q = organization ? `?organization=${encodeURIComponent(organization)}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/Issuers${q}`), token, { method: "GET" });
}

export async function createDeliveryIssuer(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Delivery/Issuers"), token, {
    method: "POST", body: JSON.stringify(body || {}),
  });
}

export async function updateDeliveryIssuer(token, id, body) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/Issuer/${encodeURIComponent(id)}`), token, {
    method: "PUT", body: JSON.stringify(body || {}),
  });
}

export async function deleteDeliveryIssuer(token, id) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/Issuer/${encodeURIComponent(id)}`), token, { method: "DELETE" });
}

export async function getDeliveryGrants(token, opts = {}) {
  const params = new URLSearchParams();
  if (opts.organization) params.set("organization", opts.organization);
  if (opts.recipientId) params.set("recipientId", opts.recipientId);
  if (opts.status) params.set("status", opts.status);
  if (opts.take != null) params.set("take", opts.take);
  const q = params.toString() ? `?${params}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/Grants${q}`), token, { method: "GET" });
}

/** Returns { grant, token, link } — the link exists only in this response. */
export async function createDeliveryGrant(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Delivery/Grants"), token, {
    method: "POST", body: JSON.stringify(body || {}),
  });
}

export async function revokeDeliveryGrant(token, id, reason) {
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/Grant/${encodeURIComponent(id)}/Revoke`), token, {
    method: "POST", body: JSON.stringify({ reason: reason || null }),
  });
}

// ── Batch / pre-archival links ────────────────────────────────────────────────────────
// Links minted before the documents exist, for a whole production cycle. There is no row per
// link anywhere, which is why revocation here works by raising an epoch fence rather than by
// marking something, and why generation streams rather than returning a document.

/** Whether a base's mapped recipient-key columns are backed by real indexes. */
export async function getDeliveryBatchReadiness(token, dbInfoId) {
  return fetchWithAuth(
    apiEndpoint(`/api/Admin/Delivery/Batch/Readiness?dbInfoId=${encodeURIComponent(dbInfoId)}`),
    token, { method: "GET" });
}

/**
 * Generates a cycle's links. Returns the RAW body — NDJSON or CSV — deliberately, rather than going
 * through fetchWithAuth: the response is a stream of lines, not a document, and JSON.parse would
 * either fail or (worse) succeed on a single-line run and hide the shape from the caller.
 *
 * The NDJSON trailer is the only way to tell a complete run from a truncated one, because the 200
 * is committed with the first byte. Callers must check it — parseBatchNdjson does.
 */
export async function generateDeliveryBatchLinks(token, body) {
  const res = await fetch(apiEndpoint("/api/Admin/Delivery/Batch"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body || {}),
  });
  const text = await res.text();
  if (!res.ok) {
    let parsed = null;
    try { parsed = JSON.parse(text); } catch { /* not a JSON error body */ }
    const error = new Error((parsed && parsed.message) || text || `Request failed: ${res.status}`);
    error.code = parsed && parsed.code ? parsed.code : null;
    error.status = res.status;
    error.details = parsed;
    throw error;
  }
  return text;
}

/**
 * Splits an NDJSON run into { header, rows, trailer }.
 *
 * A run whose trailer is missing or not ok is INCOMPLETE and must be reported as such — the links
 * that did arrive are valid, but the caller was promised a cycle and did not get one.
 */
export function parseBatchNdjson(text) {
  const out = { header: null, rows: [], trailer: null };
  for (const line of String(text || "").split("\n")) {
    const l = line.trim();
    if (!l) continue;
    let o;
    try { o = JSON.parse(l); } catch { continue; }
    if (o.type === "header") out.header = o;
    else if (o.type === "trailer") out.trailer = o;
    else if (o.token) out.rows.push(o);
  }
  out.complete = !!(out.trailer && out.trailer.ok);
  return out;
}

/** Recent generation runs — one row per run, never per link. */
export async function getDeliveryBatchRuns(token, organization, take) {
  const params = new URLSearchParams();
  if (organization) params.set("organization", organization);
  if (take != null) params.set("take", take);
  const q = params.toString() ? `?${params}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/Batch/Runs${q}`), token, { method: "GET" });
}

/** Recipient keys currently fenced, plus the organization's own epoch. */
export async function getDeliveryBatchRevocations(token, organization) {
  const q = organization ? `?organization=${encodeURIComponent(organization)}` : "";
  return fetchWithAuth(apiEndpoint(`/api/Admin/Delivery/Batch/Revocations${q}`), token, { method: "GET" });
}

// ── The question a bulk link asks before it shows anything ────────────────────────────

/** The verification question configured for one document base, or configured:false if there is none. */
export async function getDeliveryVerification(token, dbInfoId) {
  return fetchWithAuth(
    apiEndpoint(`/api/Admin/Delivery/Verification?dbInfoId=${encodeURIComponent(dbInfoId)}`),
    token, { method: "GET" });
}

/** Sets or clears it. Source "None" clears; "Field" and "Formula" are the two stored against a base. */
export async function saveDeliveryVerification(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Delivery/Verification"), token, {
    method: "PUT", body: JSON.stringify(body || {}),
  });
}

/**
 * What a formula would actually produce for real customers of this base.
 *
 * The reason the formula editor is usable at all: it answers "will my customers be able to type this?"
 * before a cycle ships, rather than after they cannot get in.
 */
export async function previewDeliveryVerification(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Delivery/Verification/Preview"), token, {
    method: "POST", body: JSON.stringify(body || {}),
  });
}

/**
 * Raises an epoch fence: one recipient key, or the whole organization.
 *
 * Omitting recipientKey means EVERY batch link the tenant ever issued, so the server requires
 * confirmOrganizationWide and the UI must not set it silently.
 */
export async function revokeDeliveryBatchLinks(token, body) {
  return fetchWithAuth(apiEndpoint("/api/Admin/Delivery/Batch/Revoke"), token, {
    method: "POST", body: JSON.stringify(body || {}),
  });
}
